/* @flow */

import adconfig from './adconfig';
import adrotation from './adrotation';
import containerHelper from './container-helper';
import { BrowserUtil, parseJSON, loadJS, loadCSS } from './utils/browserutil';
import { getSchedulerObj } from './utils/schedulehelper';
import _adrenderbase from './adserver/adrenderbase';
import { logDebug, logError } from './utils/logger';
import { extend, isString, isFunction, isObject } from './utils/coreutils';
import { getIfrDoc } from './utils/iframeutils';

/**
 * @global
 * @typedef adInfo
 * @property {string} id Unique ID of the ad position
 * @property {*} content Ad content to be rendered
 * @property {object} customVars Custom var object to be populated in iframe
 * @property {boolean} adRotation Ad rotation/not
 * @property {string} cID Container Div ID
 * @property {string} fID IFrame ID
 * @property {string} as Ad server used to fetch & render
 * @property {string} adServer Ad server used to fetch & render
 * @property {string} [sandbox] IFrame Sandbox property
 * @property {boolean} addBodyTag Should add HTML body tag
 */

/**
 * @description Implements functionality for ad loading
 * @module adloader
 * @exports adloader
 * @requires  module:utils
 * @requires  module:adconfig
 * @requires  module:adrotation
 * @requires  module:adscheduler
 * @requires  module:browserutil
 * @requires  module:adcontainer
 * @requires module:adserver
 * @requires  module:cachedAdContainer
 * @requires module:logger
 * @requires module:coreutils
 */
class _adloader {
  _doc: Document;

  _win: WindowProxy;

  _adInfo: adPosInfoWrapper;

  _adPage: string;

  _scheduler: Scheduler;

  _defConfig: adloadingConfig;

  _config: adloadingConfig;

  _site: string;

  _adrenderers: Array<adrendererInterface>;

  constructor() {
    this._adInfo = {};
    this._adPage = '';
    this._doc = document;
    this._win = window;
    this._site = '';
    this._defConfig = {
      delay: 200,
      sandbox: ''
    };
    this._config = extend(true, {}, this._defConfig);

    this._scheduler = this._getScheduler();
    const googleRender = new _adrenderbase('googleRenderer', 'googleAS');
    this._adrenderers = [googleRender];
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: WindowProxy, d: Document, g: globalParam) {
    this._adPage = g.adPage;
    this._doc = d;
    this._win = w;
    this._site = g.phInfo.site;
    containerHelper.init(w, d, g);
  }

  /**
   * setter for adloader configs
   * t - delay between loading 2 ad positions
   *
   * @param {object} o - Config
   * @returns {undefined}
   */
  updateConfig(o: adloadingConfig) {
    this._config = extend(true, {}, this._defConfig, o);
    // this._scheduler.clear();
    this._scheduler = this._getScheduler();
  }

  /**
   * prioritize above fold positions
   *
   * @param {Array<adInfo>} list - Position list
   * @returns {number} - First above the fold position index
   * @private
   */
  _prioritizeAboveFold(list: Array<adInfo>): number {
    return list.findIndex(o => adconfig.getPosSettings(o.id).aFold);
  }

  /**
   * get ad scheduler
   *
   * @returns {object} - Scheduler object
   * @private
   */
  _getScheduler(): Scheduler {
    const s = getSchedulerObj(this._scheduler);

    // set handlers in scheduler for processing, priortizing & end of processing
    s.setProperties(
      this._config.delay,
      obj => this.loadIframeAd(obj),
      list => this._prioritizeAboveFold(list),
      () => adrotation.trigger(),
      obj => this.shouldProcessImmediately(obj)
    );

    return s;
  }

  /**
   * load ad content using iframeDoc Write
   *
   * @param {adInfo} adInfoObj - AdInfo object
   * @returns {undefined}
   * @private
   */
  _loadAdContentInOldIE(adInfoObj: adInfo) {
    // caching is disabled in this loading method, using adcontainer by default
    const newIF = containerHelper.getAdContainer().loadIframeAd('', adInfoObj);
    if (!newIF) {
      return;
    }

    // let us write ad content
    const ifrDoc = getIfrDoc(newIF);
    try {
      if (ifrDoc) {
        ifrDoc.open();
        let iframeContent = `<script>window.top.__as.populateCustomVar(window, document, '${adInfoObj.id}');</script>`;
        // For Appnexus configured positions in old IE need body tag to display ad creative
        iframeContent = adInfoObj.addBodyTag
          ? `<html><head>${iframeContent}</head><body>${adInfoObj.content}</body></html>`
          : `${iframeContent}${adInfoObj.content}`;
        ifrDoc.write(iframeContent);
        // not doing ifrDoc close for old IE browsers
        if (adInfoObj.as.loadingMethod === 2 && !BrowserUtil.isOldIE()) {
          ifrDoc.close();
        }
      }
    } catch (error) {
      logError(
        `loadAdContent ${adInfoObj.id} iframe doc write failed: ${error.message}`
      );
    }
  }

  /**
   *
   * For ad servers renders creative themselves, creative is already available.
   * Let's process and show them immediately
   *
   * @param {adInfo} obj - Queued object from scheduler
   * @returns {boolean} - Whether to process immediately or not
   */
  shouldProcessImmediately(obj: adInfo = {}): boolean {
    const { adServer = '' } = obj;
    let bRet = false;
    if (adServer.length) {
      // find renderer
      bRet = this._adrenderers.some(v => v.getAS() === adServer);
    }
    return bRet;
  }

  /**
   * load iframe Ad
   *
   * @param {adInfo} adInfoObj - AdInfo object
   * @returns {HTMLIFrameElement} - Newly created iframe DOM element
   */
  loadIframeAd(adInfoObj: adInfo) {
    logDebug(`LoadingAdFor ${adInfoObj.id}`);
    // cache obj in adInfo
    this._adInfo[adInfoObj.id] = extend(true, {}, adInfoObj);

    // adserver is set. Let's consult ad server to render it.
    let bHandled = false;

    if (adInfoObj.adServer.length) {
      // always get inactive container
      const containerID =
        adInfoObj.cID ||
        containerHelper.getAdContainerInfo(adInfoObj.id, false).cID ||
        '';
      // find the adrenderer matches this
      const adrendrer = this._adrenderers.find(
        v => v.getAS() === adInfoObj.adServer
      );
      if (adrendrer) {
        bHandled = adrendrer.renderAd(adInfoObj, containerID);
        // ad is rendered in inactive container for rotating positions.
        // As the ads are rendered by ad server itself, we can't
        // have the load event to toggle the containers.
        // Let's toggle it now
        if (bHandled && adInfoObj.adRotation) {
          this.toggleAdByCID(adInfoObj.id, containerID);
        }
      }
    }

    if (bHandled) {
      return; // ad server rendered the ad. Nothing much to do here.
    }

    // load ads without blankPhnx.html only for old IE browsers
    if (adInfoObj.as.loadingMethod === 2 || BrowserUtil.isOldIE()) {
      this._loadAdContentInOldIE(adInfoObj);
      return;
    }

    // For all other browsers load ads using blankPhnx.html
    const cont = containerHelper.getContainer(adInfoObj.id);
    cont.loadIframeAd(this._adPage, adInfoObj);
  }

  /**
   * loading global script/css ad requests
   *
   * @param {string} data - Ad data
   * @returns {undefined}
   * @private
   */
  _loadGlobalScriptCssAd(data: string) {
    const info: { js: Array<string>, css: Array<string> } = extend(
      true,
      {},
      { js: [], css: [] },
      parseJSON(this._win, data)
    );
    info.js.forEach(js => loadJS(js, this._doc));
    info.css.forEach(css => loadCSS(css, this._doc));
  }

  /**
   * Populate custom var for cases where AD is load using iframeDoc.write
   *
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} id - ad position
   * @returns {undefined}
   */
  populateCustomVar(win: WindowProxy, doc: Document, id: string) {
    const info = this._adInfo[id];
    if (info === null || info === undefined) {
      return;
    }
    // set global variables
    Object.keys(info.customVars).forEach(i => {
      win[i] = info.customVars[i];
    });
  }

  /**
   * get ad info object - used for cases where ads are loaded using blankPhnx.html
   *
   * @param {string} pos - Position name
   * @returns {adInfo} - AdInfo object
   */
  getAdInfo(pos: string): adInfo {
    return this._adInfo[pos];
  }

  getIFrameAdInfo(win: WindowProxy): adInfo {
    const ret = {
      adRotation: false,
      id: '',
      content: '',
      customVars: {},
      cID: '',
      fID: '',
      adServer: '',
      addBodyTag: false,
      as: {
        as: 'openx',
        height: 1,
        width: 1
      }
    };

    // Check if it's valid iframe object & get ID
    if (!win || !win.frameElement) {
      return ret;
    }
    const adFrameId: string = win.frameElement.id || '';
    if (!adFrameId.length) {
      return ret;
    }
    // Get non-cached name and ad Info
    return this.getAdInfo(containerHelper.getNonCachedPosName(adFrameId));
  }

  /**
   * clear ad loading list
   *
   * @returns {undefined}
   */
  clearList() {
    this._scheduler.clear();
  }

  checkQueue() {
    this._scheduler.checkQueue();
  }

  /**
   *
   *
   * @param {string} pos Ad Position
   * @returns {object} AdContext info
   * @memberof _adloader
   */
  getAdContextInfo(pos: string): anyObject {
    return {
      pageName: adconfig.getPageName(),
      pos,
      site: this._site
    };
  }

  /**
   *
   * @callback contentCB
   * @param {string} pagename
   * @param {string} position
   */

  /**
   * load ad
   *
   * @param {string} pos - Ad Position
   * @param {string|contentCB} adContent - Ad content
   * @param {object} [cVar] - Custom var object
   * @param {string} adServer - Ad server name
   * @param {boolean} addBodyTag - add <body> tag around ad content
   * @param {string} cID - Container ID
   * @returns {undefined}
   */
  load(
    pos: string = '',
    adContent: string | ((string, string) => string) = '',
    cVar: CustomVars = {},
    adServer: string = '',
    addBodyTag: boolean = false,
    cID: string = ''
  ) {
    if (!pos.length) {
      return;
    }

    let content: string = '';
    if (isFunction(adContent)) {
      // $FlowFixMe
      content = adContent.call(this._win, adconfig.getPageName(), pos);
    } else if (isString(adContent)) {
      // $FlowFixMe
      content = adContent;
    }

    const as = adconfig.getPosSettings(pos);
    if (as.iframe !== undefined && !as.iframe && isString(content)) {
      // loading ads directly in the ad div without using iframe
      // no caching for non-iframe ads, loading using adcontainer directly
      containerHelper.getAdContainer().loadNonIframeAd(pos, content);
      return;
    }

    if (as.gscript !== undefined && as.gscript && isString(content)) {
      // loading global scripts/styles
      this._loadGlobalScriptCssAd(content);
      return;
    }

    const { callback: cbstr = '', iframe } = as;

    const callback = cbstr.length ? this._win[cbstr] : undefined;
    if (callback && callback !== undefined && isFunction(callback)) {
      // Trim data for callback function
      // $FlowFixMe[prop-missing]
      content = content.replaceAll(/^\s+|\s+$|(\r\n)/g, '');
      const tmp2 = callback.call(
        this._win,
        adconfig.getPageName(),
        pos,
        content
      );
      const tmp: { bHandled: boolean, data: string } = extend(
        true,
        { bHandled: false, data: content },
        tmp2
      );
      if (tmp.bHandled) {
        return;
      }

      // data might have been modified by the callback
      content = tmp.data;
    }

    const customVars = isObject(cVar) ? cVar : {};

    // add adInfo object to scheduler queue
    this._scheduler.enqueue({
      id: pos,
      content,
      customVars,
      adRotation: containerHelper.isCachingEnabled(pos, iframe),
      sandbox: this._config.sandbox,
      as,
      cID,
      fID: '',
      adServer,
      addBodyTag
    });
  }

  /**
   * toggle Ad for cached container
   *
   * @param {string} adPos - ad position
   * @returns {undefined}
   */
  toggleAD(adPos: string) {
    // Caching is disabled - no-op
    if (!containerHelper.isCachingEnabled(adPos, true)) {
      return;
    }
    logDebug(`Toggling ad for position: ${adPos}`);
    containerHelper.getCachedContainer().toggleAd(adPos);
  }

  /**
   *
   * @param {string} adPos - Ad Position
   * @param {string} activateCID  - CID to activate
   * @returns {undefined}
   */
  toggleAdByCID(adPos: string, activateCID: string) {
    // Caching is disabled - no-op
    if (!containerHelper.isCachingEnabled(adPos, true)) {
      return;
    }
    logDebug(`Toggling ad by CID for position: ${adPos}`);
    containerHelper.getCachedContainer().toggleAdByCID(adPos, activateCID);
  }
}

// export singleton object as default for this module
const al: _adloader = new _adloader();
export default al;
